/**
 * app/login/page.tsx — Stage FW-1 (Frontend Wiring 1: Auth)
 *
 * Redesigned login page with split layout:
 * - Left: Login form with email/password (validated with Zod + react-hook-form)
 * - Right: Hero image with C.L.A.S.S branding
 *
 * On success: POST /api/v1/auth/token/ → store tokens → GET /auth/me/ → store user → redirect to /
 * On 401: show inline "Invalid credentials" error.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Quote } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { postPublic, get } from '@/lib/api/client';
import { useAuthStore } from '@/lib/store/auth-slice';
import { loginWithOkta } from '@/lib/okta';
import { loginSchema, type LoginFormData } from '@/lib/validations/schemas';
import type { User } from '@/lib/types';

export default function LoginPage() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();

  const [apiError, setApiError] = useState('');
  const [loading, setLoading] = useState(false);
  const [oktaLoading, setOktaLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const handleOktaLogin = async () => {
    setApiError('');
    setOktaLoading(true);
    try {
      await loginWithOkta();
      // Redirect happens automatically
    } catch (err) {
      setOktaLoading(false);
      setApiError('Failed to initiate Okta login. Please try again.');
    }
  };

  const onSubmit = async (data: LoginFormData) => {
    setApiError('');
    setLoading(true);

    try {
      // Step 1: POST /api/v1/auth/token/ to get tokens
      const tokenResponse = await postPublic<{ access: string; refresh: string }>(
        '/api/v1/auth/token/',
        { email: data.email, password: data.password }
      );

      // Step 2: Store tokens temporarily so /auth/me/ can use them
      setAuth({
        access: tokenResponse.access,
        refresh: tokenResponse.refresh,
        user: null as unknown as User,
      });

      // Step 3: GET /api/v1/auth/me/ to fetch current user
      const user = await get<User>('/api/v1/auth/me/');

      // Step 4: Update auth state with real user
      setAuth({
        access: tokenResponse.access,
        refresh: tokenResponse.refresh,
        user,
      });

      // Step 5: Redirect to intelligence console
      navigate('/console');
    } catch (err) {
      setLoading(false);
      if (err instanceof Error && err.message.includes('401')) {
        setApiError('Invalid credentials');
      } else {
        setApiError('An error occurred. Please try again.');
      }
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-white">
      {/* Full-width Header */}
      <header className="sticky top-0 z-50 flex w-full items-center justify-between border-b border-gray-100 bg-white px-8 py-4 shadow-sm">
        <div className="flex items-center gap-3">
          {/* Product identity only: no airline is known before login. */}
          <span className="text-xl font-semibold tracking-tight text-[#072D62]">
            Rezolv CLASS
          </span>
        </div>
        <button
          type="button"
          className="rounded-full border-2 border-[#072D62] px-6 py-2.5 text-sm font-semibold text-[#072D62] transition-all duration-200 hover:bg-[#072D62] hover:text-white hover:shadow-md"
        >
          Contact Us
        </button>
      </header>

      {/* Main Content */}
      <div className="flex flex-1">
        {/* Left Side - Login Form */}
        <div className="flex w-full flex-col items-center justify-center px-8 py-16 lg:w-1/2">
          <div className="w-full max-w-md">
            {/* Welcome Text */}
            <h1 className="mb-3 font-serif text-5xl font-light italic text-[#072D62]">
              Hi there!
            </h1>
            <p className="mb-10 text-lg text-[#072D62]/80">Welcome to Rezolv CLASS.</p>

            {/* Email Field */}
            <div className="mb-4">
              <label htmlFor="email" className="sr-only">Email</label>
              <Input
                id="email"
                type="email"
                placeholder="Your Email"
                {...register('email')}
                disabled={loading}
                aria-invalid={errors.email ? 'true' : 'false'}
                aria-describedby={errors.email ? 'email-error' : undefined}
                className="h-12 rounded-none border border-gray-300 bg-white px-4 text-base placeholder:text-gray-400 focus:border-[#072D62] focus:outline-none focus:ring-1 focus:ring-[#072D62]"
              />
              {errors.email && (
                <p id="email-error" className="mt-1 text-sm text-red-600" role="alert">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div className="mb-6">
              <label htmlFor="password" className="sr-only">Password</label>
              <Input
                id="password"
                type="password"
                placeholder="Password"
                {...register('password')}
                disabled={loading}
                aria-invalid={errors.password ? 'true' : 'false'}
                aria-describedby={errors.password ? 'password-error' : undefined}
                className="h-12 rounded-none border border-gray-300 bg-white px-4 text-base placeholder:text-gray-400 focus:border-[#072D62] focus:outline-none focus:ring-1 focus:ring-[#072D62]"
              />
              {errors.password && (
                <p id="password-error" className="mt-1 text-sm text-red-600" role="alert">
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* API Error Message */}
            {apiError && (
              <div className="mb-6 border-l-4 border-red-500 bg-red-50 px-4 py-3 text-sm text-red-600" role="alert">
                {apiError}
              </div>
            )}

            {/* Login Button */}
            <Button
              type="submit"
              onClick={handleSubmit(onSubmit)}
              disabled={loading || oktaLoading}
              className="h-12 w-full rounded-none bg-[#072D62] text-base font-semibold tracking-wide text-white shadow-md transition-all duration-200 hover:bg-[#051E4A] hover:shadow-lg disabled:opacity-70"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Log In'
              )}
            </Button>

            {/* SSO Divider */}
            <div className="my-6 flex items-center gap-4">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-sm text-gray-400">or</span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>

            {/* Okta SSO Button */}
            <Button
              onClick={handleOktaLogin}
              disabled={loading || oktaLoading}
              variant="outline"
              className="h-12 w-full rounded-none border-2 border-[#072D62] bg-white text-base font-semibold tracking-wide text-[#072D62] transition-all duration-200 hover:bg-[#072D62] hover:text-white disabled:opacity-70"
            >
              {oktaLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Redirecting to Okta...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-3">
                  <img
                    src="/images/okta-logo.png"
                    alt="Okta"
                    width={24}
                    height={24}
                    className="h-6 w-6"
                  />
                  Sign in with Okta
                </span>
              )}
            </Button>
          </div>
        </div>

        {/* Right Side - Hero Image */}
        <div className="relative hidden lg:block lg:w-1/2">
          {/* Background Image */}
          <img
            src="/images/login-bg1.jpg"
            alt="Airplane wing at sunset"
            className="absolute inset-0 h-full w-full object-cover"
            loading="eager"
          />

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10" />

          {/* Content */}
          <div className="absolute bottom-0 left-0 right-0 p-14">
            {/* Quote Icon */}
            <div className="mb-8">
              <Quote className="h-14 w-14 fill-white/90 text-white/90 drop-shadow-lg" />
            </div>

            {/* C.L.A.S.S Heading */}
            <h2 className="mb-5 text-6xl font-bold tracking-widest text-white drop-shadow-lg">
              C.L.A.S.S
            </h2>

            {/* Tagline */}
            <p className="text-2xl font-light leading-relaxed text-white/95 drop-shadow-md">
              Crew Layovers Automated Smart
              <br />
              Solution.
            </p>

            {/* Decorative line */}
            <div className="mt-8 h-1 w-24 rounded-full bg-white/40" />
          </div>
        </div>
      </div>
    </div>
  );
}
